/**
 * @file buildPayload.js
 * @description Pure mapping from UI form state → ZATCA invoice payload.
 *
 * Backend company/branch context:
 *  - deviceId is sent explicitly.
 *  - companyId is NOT sent; backend derives it from selected-company cookie.
 *  - branchId is NOT sent; backend derives it from the selected device.
 *
 * No React. No state. No side effects.
 */

import { getPaymentMeansCode, isBankPayment } from "./paymentMeans";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getInvoiceTypeCode = (type) => {
  switch (type) {
    case "credit-note":
      return "381";
    case "debit-note":
      return "383";
    default:
      return "388";
  }
};

const getDeviceObjectId = (selectedDevice) =>
  selectedDevice?._id || selectedDevice?.id || selectedDevice?.deviceId;

// ---------------------------------------------------------------------------
// Public
// ---------------------------------------------------------------------------

/**
 * Build the full ZATCA invoice payload to send to the backend.
 *
 * @param {{
 *   formData:       object,
 *   selectedDevice: object,
 *   company:        object,
 * }} args
 * @returns {object} ZATCA-compliant invoice payload
 */
export const buildInvoicePayload = ({ formData, selectedDevice, company }) => ({
  deviceId: getDeviceObjectId(selectedDevice),
  clientId: formData.customer.clientId || null,

  invoiceHeader: {
    issueDate: formData.invoice.date,
    issueTime: formData.invoice.time,
    invoiceTypeCode: getInvoiceTypeCode(formData.invoice.type),
    invoiceTypeTransaction:
      formData.customer.type === "individual" ? "0200000" : "0100000",
    notes: formData.invoice.notes ? [formData.invoice.notes] : [],
    documentCurrencyCode: formData.invoice.currency,
    taxCurrencyCode: "SAR",
  },

  processControl: { profileId: "reporting:1.0" },

  ...(formData.invoice.type === "credit-note" ||
  formData.invoice.type === "debit-note"
    ? {
        billingReference: {
          invoiceDocumentReference: {
            id: formData.invoice.referenceNumber || "",
          },
        },
      }
    : {}),

  seller: {
    vatNumber: formData.seller.vatNumber || company.vatNumber,
    registrationNumber: {
      id:
        formData.seller.registrationNumber?.id ||
        company.registrationNumber?.id,
      schemeId:
        formData.seller.registrationNumber?.schemeId ||
        company.registrationNumber?.schemeId ||
        "CRN",
    },
    registrationName: formData.seller.registrationName || company.legalName,
    address: {
      streetName:
        formData.seller.address?.street || company.address?.street || "",
      additionalStreetName:
        formData.seller.address?.district || company.address?.district || "",
      buildingNumber:
        formData.seller.address?.buildingNumber ||
        company.address?.buildingNumber ||
        "",
      plotIdentification:
        formData.seller.address?.plotIdentification ||
        company.address?.plotIdentification ||
        "",
      cityName: formData.seller.address?.city || company.address?.city || "",
      citySubdivisionName:
        formData.seller.address?.citySubdivision ||
        company.address?.district ||
        "",
      countrySubentity:
        formData.seller.address?.countrySubentity ||
        formData.seller.address?.province ||
        company.address?.province ||
        "",
      postalZone:
        formData.seller.address?.postalCode ||
        company.address?.postalCode ||
        "",
      countryCode:
        formData.seller.address?.countryCode ||
        company.address?.country ||
        "SA",
    },
    otherIdentifiers:
      formData.seller.otherIdentifiers || company.otherIdentifiers || [],
  },

  buyer: {
    vatNumber: formData.customer.vatNumber || "",
    registrationNumber: {
      id: formData.customer.crNumber || "",
      schemeId: formData.customer.registrationNumber?.schemeId || "CRN",
    },
    registrationName: formData.customer.name,
    address: {
      streetName: formData.customer.address.street,
      additionalStreetName: formData.customer.address.district,
      buildingNumber: formData.customer.address.buildingNumber || "",
      plotIdentification: formData.customer.address.plotIdentification || "",
      cityName: formData.customer.address.city,
      citySubdivisionName: formData.customer.address.citySubdivision || "",
      countrySubentity: formData.customer.address.province || "",
      postalZone: formData.customer.address.postalCode,
      countryCode: formData.customer.address.country || "SA",
    },
    contact: {
      email: formData.customer.contact?.email || "",
      phone: formData.customer.contact?.phone || "",
    },
    otherIdentifiers: [],
  },

  delivery: {
    actualDeliveryDate:
      formData.invoice.actualDeliveryDate || formData.invoice.date,
    latestDeliveryDate: formData.invoice.latestDeliveryDate || "",
    dueDate: formData.invoice.dueDate || "",
  },

  paymentMeans: [
    {
      paymentMeansCode: getPaymentMeansCode(formData.invoice.paymentMode),
      paymentNote: formData.invoice.paymentNote || "",
      ...(isBankPayment(formData.invoice.paymentMode) && {
        payeeFinancialAccount: {
          iban: formData.invoice.iban || company.iban || "",
        },
      }),
    },
  ],

  allowancesCharges: formData.invoice.documentAllowanceChargeIndicator
    ? [
        {
          chargeIndicator:
            formData.invoice.documentAllowanceChargeIndicator === "true",
          amount:
            parseFloat(formData.invoice.documentAllowanceChargeAmount) || 0,
          baseAmount:
            parseFloat(formData.invoice.documentAllowanceChargeBaseAmount) || 0,
          multiplierFactorNumeric:
            parseFloat(formData.invoice.documentAllowanceChargeMultiplier) || 0,
          allowanceChargeReason:
            formData.invoice.documentAllowanceChargeReason || "",
          allowanceChargeReasonCode:
            formData.invoice.documentAllowanceChargeReasonCode || "",
          taxCategory: (() => {
            const std = formData.lineItems.find((i) => !i.taxExempt);
            return {
              id: std ? std.vatCategoryCode || "S" : "S",
              percent: std ? parseFloat(std.taxRate) || 0 : 0,
            };
          })(),
        },
      ]
    : [],

  invoiceTotals: {},
  vatBreakdown: [],

  invoiceLines: formData.lineItems.map((item, index) => ({
    lineId: (index + 1).toString(),
    invoicedQuantity: parseFloat(item.quantity) || 1,
    unitCode: "PCE",
    lineExtensionAmount: 0,
    item: {
      name: item.description,
      classifiedTaxCategory: (() => {
        const taxRate = parseFloat(item.taxRate) || 0;
        const isZeroVat = taxRate === 0 || item.taxExempt;

        return {
          id: isZeroVat
            ? item.vatCategoryCode || ""
            : item.vatCategoryCode || "S",
          percent: taxRate,
          exemptionReason: isZeroVat ? item.exemptionReason || "" : undefined,
          exemptionReasonCode: isZeroVat
            ? item.exemptionReasonCode || ""
            : undefined,
        };
      })(),
    },
    price: {
      priceAmount: parseFloat(item.unitPrice) || 0,
      baseQuantity: 1,
    },
    allowancesCharges: item.allowanceChargeIndicator
      ? [
          {
            chargeIndicator: item.allowanceChargeIndicator === "true",
            amount: parseFloat(item.allowanceChargeAmount) || 0,
            baseAmount: parseFloat(item.allowanceChargeBaseAmount) || 0,
            multiplierFactorNumeric:
              parseFloat(item.allowanceChargeMultiplier) || 0,
            allowanceChargeReason: item.allowanceChargeReason || "",
            allowanceChargeReasonCode: item.allowanceChargeReasonCode || "",
            taxCategory: {
              id: item.taxExempt ? "E" : "S",
              percent: parseFloat(item.taxRate) || 0,
            },
          },
        ]
      : [],
    taxTotal: {},
  })),
});
